import React from 'react';
import { Navigate } from 'react-router-dom';
import { LoadingSpinner } from '../components/ui';
import { useDeskAdmin } from '../hooks/useDeskAdmin';

const ProductDocumentDashboard: React.FC = () => {
  const { isAdmin, isLoading } = useDeskAdmin();

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard/main" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 font-sans antialiased" dir="rtl">
      {/* Header */}
      <header className="text-center mb-12 pb-6 border-b border-gray-200">
        <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 tracking-tight">سند محصول پلتفرم Smart Insight</h1>
        <h2 className="text-2xl md:text-3xl font-semibold mt-6 mb-2 text-gray-800">پلتفرم جامع هوش تجاری و تحلیل داده</h2>
        <p className="text-lg text-gray-600 italic max-w-2xl mx-auto">جایی که داده به بینش و بینش به موفقیت تبدیل می‌شود</p>
      </header>

      {/* Table of Contents */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 mb-12 shadow-sm border border-gray-200">
        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 border-b border-gray-200 pb-4">فهرست مطالب</h2>
        <ol className="list-decimal list-inside space-y-3 text-blue-600 font-medium hover:text-blue-700 transition-colors" dir="rtl">
          <li><a href="#intro" className="hover:underline">معرفی محصول</a></li>
          <li><a href="#features" className="hover:underline">ویژگی‌ها و قابلیت‌ها</a></li>
          <li><a href="#architecture" className="hover:underline">معماری فنی</a></li>
          <li><a href="#audience" className="hover:underline">مخاطبان هدف</a></li>
          <li><a href="#benefits" className="hover:underline">مزایای کلیدی</a></li>
          <li><a href="#dashboards" className="hover:underline">داشبوردهای اصلی</a></li>
          <li><a href="#roadmap" className="hover:underline">نقشه راه محصول</a></li>
          <li><a href="#contact" className="hover:underline">تماس با ما</a></li>
        </ol>
      </div>

      {/* Introduction */}
      <section id="intro" className="mb-16 scroll-mt-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-6 pb-2 border-b border-gray-200 text-gray-900 scroll-mt-16">معرفی محصول</h2>
        
        <div className="mb-12 relative">
          <h3 className="text-2xl md:text-2.5xl font-semibold mb-4 text-gray-800 mt-8">Smart Insight چیست؟</h3>
          <p className="text-gray-700 leading-relaxed text-lg mb-6 prose prose-gray max-w-none">
            <strong>Smart Insight</strong> یک پلتفرم جامع هوش تجاری (Business Intelligence) است که با استفاده از تکنولوژی‌های مدرن و هوش مصنوعی، داده‌های خام کسب‌وکار شما را به بینش‌های عملی و ارزشمند تبدیل می‌کند. این پلتفرم با ارائه بیش از 10 داشبورد تخصصی، امکان تحلیل همه‌جانبه عملکرد سازمان را فراهم می‌آورد.
          </p>
        </div>

        <div className="mb-12 relative">
          <h3 className="text-2xl md:text-2.5xl font-semibold mb-4 text-gray-800 mt-8">چشم‌انداز ما</h3>
          <p className="text-gray-700 leading-relaxed text-lg mb-6 max-w-4xl">
            ما معتقدیم که هر تصمیم تجاری باید بر پایه داده‌های دقیق و تحلیل‌های عمیق اتخاذ شود. Smart Insight با دموکراتیزه کردن دسترسی به داده‌ها و تحلیل‌ها، به سازمان‌ها کمک می‌کند تا:
          </p>
          <ul className="list-none space-y-4 mx-6 text-lg max-w-3xl" dir="rtl">
            <li className="flex items-start">
              <span className="text-green-500 mt-1 ml-2 flex-shrink-0">🎯</span>
              <span>تصمیمات آگاهانه‌تر بگیرند</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mt-1 ml-2 flex-shrink-0">📈</span>
              <span>عملکرد خود را بهینه کنند</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mt-1 ml-2 flex-shrink-0">🏆</span>
              <span>رضایت مشتریان را افزایش دهند</span>
            </li>
          </ul>
        </div>

        <div dir="rtl">
          <h3 className="text-2xl md:text-2.5xl font-semibold mb-4 text-gray-800 mt-8">ارزش پیشنهادی کلیدی</h3>
          <ol className="list-decimal list-inside space-y-3 text-gray-700 text-lg max-w-4xl">
            <li><strong>تحلیل دوره‌ای</strong>: داده‌های به‌روز ماهانه برای تصمیم‌گیری آگاهانه</li>
            <li><strong>رابط کاربری ساده</strong>: طراحی شهودی که نیاز به آموزش پیچیده ندارد</li>
            <li><strong>قابلیت شخصی‌سازی</strong>: داشبوردهای قابل تنظیم برای نیازهای خاص هر کاربر</li>
          </ol>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mb-16 scroll-mt-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-6 pb-2 border-b border-gray-200 text-gray-900 scroll-mt-16">ویژگی‌ها و قابلیت‌ها</h2>
        
        <div className="mb-12 relative">
          <h3 className="text-2xl md:text-2.5xl font-semibold mb-4 text-gray-800 mt-8">🎯 داشبوردهای تحلیلی جامع</h3>
          <p className="text-gray-700 mb-6 text-lg max-w-4xl">Smart Insight مجموعه‌ای کامل از داشبوردهای تخصصی ارائه می‌دهد:</p>
          
          <div className="space-y-8 mx-6 max-w-5xl mx-auto">
            <div className="group hover:bg-blue-50 rounded-lg p-6 border-r-4 border-blue-500 pr-4 transition-colors duration-200">
              <h4 className="text-xl md:text-2xl font-semibold mb-3 text-gray-900 group-hover:text-blue-600 transition-colors">1. داشبورد عملکرد کلی</h4>
              <ul className="list-disc list-inside ml-4 text-gray-700 space-y-2 font-medium">
                <li>نمای 360 درجه از عملکرد کسب‌وکار</li>
                <li>KPI های کلیدی در یک نگاه</li>
                <li>تحلیل روند فروش و درآمد</li>
              </ul>
            </div>

            <div className="group hover:bg-green-50 rounded-lg p-6 border-r-4 border-green-500 pr-4 transition-colors duration-200">
              <h4 className="text-xl md:text-2xl font-semibold mb-3 text-gray-900 group-hover:text-green-600 transition-colors">2. داشبورد رضایت مشتریان</h4>
              <ul className="list-disc list-inside ml-4 text-gray-700 space-y-2 font-medium">
                <li>امتیاز کلی رضایت (CSAT)</li>
                <li>تحلیل بر اساس شیفت‌های کاری</li>
                <li>رضایت بر اساس نوع سفارش</li>
                <li>روند رضایت در طول زمان</li>
              </ul>
            </div>

            <div className="group hover:bg-purple-50 rounded-lg p-6 border-r-4 border-purple-500 pr-4 transition-colors duration-200">
              <h4 className="text-xl md:text-2xl font-semibold mb-3 text-gray-900 group-hover:text-purple-600 transition-colors">3. داشبورد منابع انسانی</h4>
              <ul className="list-disc list-inside ml-4 text-gray-700 space-y-2 font-medium">
                <li>ترکیب و توزیع نیروی انسانی</li>
                <li>عملکرد پرسنل به تفکیک نقش</li>
                <li>شناسایی پرسنل برتر</li>
                <li>تحلیل بهره‌وری</li>
              </ul>
            </div>

            <div className="group hover:bg-yellow-50 rounded-lg p-6 border-r-4 border-yellow-500 pr-4 transition-colors duration-200">
              <h4 className="text-xl md:text-2xl font-semibold mb-3 text-gray-900 group-hover:text-yellow-600 transition-colors">4. داشبورد باشگاه مشتریان</h4>
              <ul className="list-disc list-inside ml-4 text-gray-700 space-y-2 font-medium">
                <li>مدیریت برنامه‌های وفاداری</li>
                <li>تحلیل رفتار اعضا</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Architecture */}
      <section id="architecture" className="mb-16 scroll-mt-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-6 pb-2 border-b border-gray-200 text-gray-900 scroll-mt-16">معماری فنی</h2>
        
        <div className="mb-12 relative">
          <h3 className="text-2xl md:text-2.5xl font-semibold mb-4 text-gray-800 mt-8">🎨 Frontend Architecture</h3>
          <p className="text-gray-700 mb-4 text-lg max-w-4xl">Smart Insight با جدیدترین تکنولوژی‌های frontend ساخته شده است:</p>
          
          <div className="bg-gray-50 p-6 rounded-xl mb-6 font-mono text-sm overflow-auto border border-gray-200 shadow-sm">
            {`{
  "framework": "React 19.1",
  "language": "TypeScript 5.8",
  "styling": "TailwindCSS 3.4",
  "charts": "Chart.js 4.4",
  "state": "React Query 5.76",
  "routing": "React Router 6.30"
}`}
          </div>
        </div>

        <div className="mb-12 relative">
          <h3 className="text-2xl md:text-2.5xl font-semibold mb-4 text-gray-800 mt-8">⚙️ Backend Architecture</h3>
          <p className="text-gray-700 mb-4 text-lg max-w-4xl">Backend با تمرکز بر عملکرد و مقیاس‌پذیری طراحی شده:</p>
          
          <div className="bg-gray-50 p-6 rounded-xl mb-6 font-mono text-sm overflow-auto border border-gray-200 shadow-sm">
            {`{
  "framework": "FastAPI",
  "language": "Python 3.11",
  "database": "SQL Server",
  "cache": "Redis",
  "auth": "JWT + Magic Link"
}`}
          </div>
        </div>

        <div dir="rtl">
          <h3 className="text-2xl md:text-2.5xl font-semibold mb-4 text-gray-800 mt-8">🔒 امنیت و احراز هویت</h3>
          <ul className="list-disc list-inside ml-4 text-gray-700 space-y-3 text-lg max-w-4xl">
            <li><strong>JWT Authentication</strong>: توکن‌های امن با expire time</li>
            <li><strong>Magic Link</strong>: ورود بدون پسورد برای امنیت بیشتر</li>
            <li><strong>Role-Based Access Control</strong>: کنترل دسترسی بر اساس نقش</li>
          </ul>
        </div>
      </section>

      {/* Target Audience */}
      <section id="audience" className="mb-16 scroll-mt-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-6 pb-2 border-b border-gray-200 text-gray-900 scroll-mt-16">مخاطبان هدف</h2>
        <p className="text-gray-700 mb-6 text-lg max-w-4xl">Smart Insight به طور خاص برای مدیران و صاحبان کسب‌وکار در حوزه صنایع غذایی طراحی شده است:</p>
        
        <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg backdrop-blur-sm" dir="rtl">
          <h3 className="text-2xl md:text-3xl font-semibold mb-6 pb-2 border-b border-gray-300 text-gray-900">🍽️ صنایع غذایی (رستوران‌ها، کافه‌ها، فست‌فودها، قنادی‌ها و کترینگ‌ها)</h3>
          
          <div className="space-y-8 max-w-5xl mx-auto">
            <div>
              <h4 className="text-xl md:text-2xl font-semibold mb-3 text-gray-800 mt-4">نیازها:</h4>
              <ul className="list-disc list-inside ml-4 text-gray-700 space-y-3 text-lg max-w-4xl">
                <li>مدیریت شعب متعدد و کنترل کیفیت</li>
                <li>تحلیل ساعات شلوغی و بهینه‌سازی شیفت‌ها</li>
                <li>افزایش رضایت مشتری و وفاداری</li>
                <li>تحلیل عملکرد پرسنل و بهره‌وری</li>
              </ul>
            </div>

            <div>
              <h4 className="text-xl md:text-2xl font-semibold mb-3 text-gray-800 mt-4">داشبوردها:</h4>
              <ul className="list-disc list-inside ml-4 text-gray-700 space-y-3 text-lg max-w-4xl">
                <li>داشبورد عملکرد کلی (تحلیل فروش و KPIها)</li>
                <li>داشبورد رضایت مشتری (پایش کیفیت خدمات)</li>
                <li>داشبورد منابع انسانی (عملکرد پرسنل و شیفت‌ها)</li>
                <li>داشبورد باشگاه مشتریان (برنامه‌های وفاداری)</li>
                <li>داشبورد تحلیل مشتریان (رفتار خرید)</li>
                <li>نقشه حرارتی ارزیابی (عملکرد شعب)</li>
                <li>داشبورد شخصی‌سازی شده (نیازهای خاص)</li>
              </ul>
            </div>

            <div>
              <h4 className="text-xl md:text-2xl font-semibold mb-3 text-gray-800 mt-4">ارزش:</h4>
              <ul className="list-disc list-inside ml-4 text-gray-700 space-y-3 text-lg max-w-4xl">
                <li>افزایش رضایت مشتری و نرخ بازگشت</li>
                <li>بهبود زمان سرویس‌دهی و کیفیت</li>
                <li>تصمیم‌گیری آگاهانه بر اساس داده‌های دقیق</li>
                <li>استانداردسازی فرآیندها در تمام شعب</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Main Dashboards */}
      <section id="dashboards" className="mb-16 scroll-mt-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-6 pb-2 border-b border-gray-200 text-gray-900 scroll-mt-16">داشبوردهای اصلی</h2>
        
        <div className="space-y-10 max-w-6xl mx-auto">
          {/* Dashboard 1 */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <h3 className="text-xl md:text-2xl font-semibold mb-4 pb-2 border-b border-gray-200 text-gray-900">1. داشبورد عملکرد کلی (Main Performance Dashboard)</h3>
            <div className="mb-6 pt-4">
              <h4 className="font-semibold text-gray-900 mb-2 text-lg">کاربرد:</h4>
              <p className="text-gray-700 text-lg leading-relaxed">مرکز فرماندهی کسب‌وکار شما - تمام اطلاعات حیاتی در یک نگاه</p>
            </div>
            <div className="mb-6 pt-4">
              <h4 className="font-semibold text-gray-900 mb-2 text-lg">ویژگی‌های کلیدی:</h4>
              <ul className="list-disc list-inside ml-4 text-gray-700 space-y-2 text-lg font-medium" dir="rtl">
                <li>📊 نمودار روند فروش: مقایسه دوره‌ای با قابلیت drill-down</li>
                <li>📈 KPIs اصلی: درآمد، تعداد سفارش، میانگین سبد</li>
                <li>😊 رضایت مشتری: ادغام با معیارهای عملکرد</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2 text-lg">مناسب برای:</h4>
              <p className="text-gray-700 text-lg leading-relaxed">مدیران ارشد، مدیران فروش، تیم‌های استراتژی</p>
            </div>
          </div>

          {/* Dashboard 2 */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <h3 className="text-xl md:text-2xl font-semibold mb-4 pb-2 border-b border-gray-200 text-gray-900">2. داشبورد رضایت مشتریان (Satisfaction Dashboard)</h3>
            <div className="mb-6 pt-4">
              <h4 className="font-semibold text-gray-900 mb-2 text-lg">کاربرد:</h4>
              <p className="text-gray-700 text-lg leading-relaxed">پایش مستمر تجربه مشتری و شناسایی نقاط بهبود</p>
            </div>
            <div className="mb-6 pt-4">
              <h4 className="font-semibold text-gray-900 mb-2 text-lg">ویژگی‌های کلیدی:</h4>
              <ul className="list-disc list-inside ml-4 text-gray-700 space-y-2 text-lg font-medium" dir="rtl">
                <li>⭐ امتیاز رضایت کلی: CSAT, CES</li>
                <li>🕐 تحلیل شیفت: عملکرد در زمان‌های مختلف</li>
                <li>📝 تحلیل نوع سفارش: رضایت در کانال‌های مختلف</li>
                <li>📉 روند تاریخی: مقایسه با دوره‌های قبل</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2 text-lg">مناسب برای:</h4>
              <p className="text-gray-700 text-lg leading-relaxed">مدیران CX، تیم‌های کیفیت، مدیران عملیات</p>
            </div>
          </div>

          {/* Dashboard 3 */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <h3 className="text-xl md:text-2xl font-semibold mb-4 pb-2 border-b border-gray-200 text-gray-900">3. داشبورد منابع انسانی (HR Dashboard)</h3>
            <div className="mb-6 pt-4">
              <h4 className="font-semibold text-gray-900 mb-2 text-lg">کاربرد:</h4>
              <p className="text-gray-700 text-lg leading-relaxed">مدیریت و توسعه سرمایه انسانی سازمان</p>
            </div>
            <div className="mb-6 pt-4">
              <h4 className="font-semibold text-gray-900 mb-2 text-lg">ویژگی‌های کلیدی:</h4>
              <ul className="list-disc list-inside ml-4 text-gray-700 space-y-2 text-lg font-medium" dir="rtl">
                <li>👥 ترکیب تیم: توزیع بر اساس نقش و بخش</li>
                <li>🏆 رتبه‌بندی عملکرد: شناسایی پرسنل برتر</li>
                <li>📊 تحلیل بهره‌وری: مقایسه بین تیم‌ها</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2 text-lg">مناسب برای:</h4>
              <p className="text-gray-700 text-lg leading-relaxed">مدیران HR، مدیران تیم</p>
            </div>
          </div>

          {/* Dashboard 4 */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <h3 className="text-xl md:text-2xl font-semibold mb-4 pb-2 border-b border-gray-200 text-gray-900">4. داشبورد باشگاه مشتریان (Club Dashboard)</h3>
            <div className="mb-6 pt-4">
              <h4 className="font-semibold text-gray-900 mb-2 text-lg">کاربرد:</h4>
              <p className="text-gray-700 text-lg leading-relaxed">مدیریت جامع برنامه‌های وفاداری و CRM</p>
            </div>
            <div className="mb-6 pt-4">
              <h4 className="font-semibold text-gray-900 mb-2 text-lg">ویژگی‌های کلیدی:</h4>
              <ul className="list-disc list-inside ml-4 text-gray-700 space-y-2 text-lg font-medium" dir="rtl">
                <li>💳 مدیریت اعتبار: نرخ استفاده و الگوهای مصرف</li>
                <li>📱 رفتار اعضا: تحلیل خرید و بازدید</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2 text-lg">مناسب برای:</h4>
              <p className="text-gray-700 text-lg leading-relaxed">مدیران بازاریابی، تیم CRM، مدیران برنامه وفاداری</p>
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section id="roadmap" className="mb-16 scroll-mt-16">
        <h2 className="text-3xl md:text-4xl font-bold mb-6 pb-2 border-b border-gray-200 text-gray-900 scroll-mt-16">نقشه راه محصول</h2>
        
        <div className="mb-12 relative">
          <h3 className="text-2xl md:text-2.5xl font-semibold mb-4 text-gray-800 mt-8">🎯 وضعیت فعلی</h3>
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-8 rounded-2xl border border-green-200 shadow-inner">
            <p className="font-semibold text-green-800 mb-3 text-lg">✅ قابلیت‌های موجود:</p>
            <ul className="list-disc list-inside ml-4 text-gray-700 space-y-3 text-lg font-medium max-w-4xl mx-auto" dir="rtl">
              <li>10+ داشبورد تخصصی</li>
              <li>تحلیل دوره‌ای</li>
              <li>پشتیبانی موبایل</li>
              <li>API های یکپارچگی</li>
              <li>احراز هویت پیشرفته</li>
            </ul>
          </div>
        </div>

        <div dir="rtl">
          <h3 className="text-2xl md:text-2.5xl font-semibold mb-4 text-gray-800 mt-8">🚀 برنامه 6 ماهه</h3>
          <div className="bg-gradient-to-r from-blue-50 to-indigo-100 p-8 rounded-2xl border border-blue-200 shadow-inner">
            <p className="font-semibold text-blue-800 mb-3 text-lg">🔄 در حال توسعه:</p>
            <h4 className="text-lg md:text-xl font-semibold mb-3 text-blue-800 mt-4">🧠 قابلیت‌های هوش مصنوعی</h4>
            <ul className="list-disc list-inside ml-4 text-gray-700 space-y-3 text-lg font-medium max-w-4xl mx-auto">
              <li>پیش‌بینی روندها: الگوریتم‌های ML برای پیش‌بینی فروش و تقاضا</li>
              {/* <li>تشخیص ناهنجاری: شناسایی خودکار الگوهای غیرعادی</li> */}
              <li>توصیه‌های هوشمند: پیشنهادات عملی بر اساس تحلیل داده‌ها</li>
              <li>پردازش زبان طبیعی: تحلیل نظرات و بازخوردهای مشتریان</li>
              <li>AI Assistant: دستیار هوشمند برای تحلیل</li>
              <li>Advanced ML: پیش‌بینی‌های دقیق‌تر</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="mb-16 scroll-mt-16">
        <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-3xl p-10 md:p-12 text-center border border-blue-100 shadow-xl backdrop-blur-sm">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 tracking-tight">🌟 آماده تحول دیجیتال کسب‌وکار خود هستید؟</h2>
          <p className="text-xl md:text-2xl mb-6 text-gray-700 font-medium">برای اطلاعات بیشتر و درخواست Demo:</p>
          <p className="text-xl md:text-2xl mb-8 font-semibold text-blue-600 hover:text-blue-700 transition-colors" dir="rtl">
            🌐 <a href="https://smartx.ir/contact/" className="text-blue-600 hover:underline font-semibold">صفحه تماس با ما</a>
          </p>
          <p className="text-lg md:text-xl mb-8 text-gray-600">تیم فروش آماده پاسخگویی به سوالات شماست</p>
          <a href="https://smartx.ir/contact/" className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105">
            درخواست Demo
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-center mt-16 pt-8 border-t border-gray-200 text-gray-600 max-w-2xl mx-auto">
        <p className="italic text-xl md:text-2xl font-medium text-gray-700">Smart Insight - پلتفرمی که با کسب‌وکار شما رشد می‌کند</p>
        <p className="font-semibold mt-4 text-lg md:text-xl text-gray-800">Version 1.0 | آذر 1403</p>
      </footer>
    </div>
  );
};

export default ProductDocumentDashboard;